import { DeviceTabletSpeaker, X } from '@phosphor-icons/react';
import { Button, Heading, Icon, IconButton, Surface, Text, TextField, buttonStyles } from '@shh/ui';
import { signOut } from 'firebase/auth';
import { useState } from 'react';
import { Link } from 'react-router';
import { createPairingCode, friendlyError } from '../data/api';
import { useFamily } from '../data/family';
import { firebase } from '../lib/firebase';
import { useTip } from '../lib/tips';
import { startText } from '../i18n/start';
import { todayText } from '../i18n/today';

/** A touch screen of tablet size – where a shared family device usually is. */
export const isTabletLike = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: coarse)').matches &&
  Math.min(window.screen.width, window.screen.height) >= 600;

/**
 * Offer the family mode where it fits (1.29, Mike 28.10.: "eingeloggt auf dem Tablet sieht es
 * genau wie der Wandmodus aus" – 0 of 9 families had paired a device). On a tablet a parent
 * switches this very device in one go; on a phone, while no device is paired, the card points to
 * the settings. Not "an der Wand" only: the kitchen, the hall, the couch (Mike, 28.10.).
 */
export function FamilyModeOffer() {
  const f = useFamily();
  const tip = useTip('familyMode');
  const [tablet] = useState(isTabletLike);
  const [asking, setAsking] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const t = todayText.familyMode;
  if (!tip.show || (!tablet && f.devices.length > 0)) return null;

  /** The code goes straight to the pairing page after signing out – it pairs by itself. */
  const switchThisDevice = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const { code } = await createPairingCode(name.trim() || startText.wallName);
      tip.dismiss();
      await signOut(firebase().auth);
      window.location.assign(`/koppeln?code=${code}`);
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  };

  return (
    <Surface tone="sunken" className="flex flex-col gap-gap-sm" role="region" aria-label={t.title}>
      <div className="flex items-start gap-gap-sm">
        <Icon icon={DeviceTabletSpeaker} size="lg" className="text-accent-secondary" />
        <div className="flex flex-1 flex-col gap-gap-xs">
          <Heading level={2} size="sm">
            {tablet ? t.tabletTitle : t.title}
          </Heading>
          <Text size="sm" tone="secondary">
            {t.text}
          </Text>
        </div>
        <IconButton icon={X} aria-label={t.later} onPress={tip.dismiss} />
      </div>
      {tablet && asking ? (
        <div className="flex flex-col gap-gap-sm">
          <Text size="sm">{t.signOutNote}</Text>
          <div className="flex flex-wrap items-end gap-gap-sm">
            <TextField
              label={t.name}
              placeholder={t.namePlaceholder}
              value={name}
              onChange={setName}
              maxLength={40}
            />
            <Button isDisabled={busy} onPress={() => void switchThisDevice()}>
              {t.switchNow}
            </Button>
            <Button variant="quiet" isDisabled={busy} onPress={() => setAsking(false)}>
              {t.cancel}
            </Button>
          </div>
          {error && <p className="text-sm font-semibold text-feedback-danger">{error}</p>}
        </div>
      ) : (
        <div className="flex flex-wrap gap-gap-sm">
          {tablet && <Button onPress={() => setAsking(true)}>{t.switchThis}</Button>}
          <Link
            to="/einstellungen#geraete"
            className={buttonStyles({ variant: tablet ? 'quiet' : 'primary' })}
          >
            {tablet ? t.pairOther : t.pairDevice}
          </Link>
        </div>
      )}
    </Surface>
  );
}
